import type { Router } from 'vue-router';
import type { useSessionStore } from '../../stores/useSessionStore';
import { WARNING_AT_MS, END_AT_MS } from '../../stores/useSessionStore';
import { ref } from 'vue';

type WindowLike = Pick<
  Window,
  | 'addEventListener'
  | 'removeEventListener'
  | 'setTimeout'
  | 'clearTimeout'
  | 'setInterval'
  | 'clearInterval'
>;

export type UseSessionInactivityDeps = {
  store: ReturnType<typeof useSessionStore>;
  router: Pick<Router, 'push'>;
  countdownEnabled?: boolean;
  tickEveryMs?: number;
  srUpdateEveryMs?: number;
  warningAtMs?: number;
  endAtMs?: number;
  events?: string[];
  sessionEndedPath?: string;
  clearAppSessionData?: () => void;
  win?: WindowLike;
  now?: () => number;
  // ignore events in...
  ignoreSelectors?: string[];
  ignoreEvent?: (e: Event) => boolean;
  //
  closeWarningUi?: () => void;
};

const DEFAULT_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function useSessionInactivity(deps: UseSessionInactivityDeps) {
  const win = deps.win ?? window;
  const now = deps.now ?? (() => Date.now());

  const countdownEnabled = deps.countdownEnabled ?? false;
  const tickEveryMs = deps.tickEveryMs ?? 1000;
  const srUpdateEveryMs = deps.srUpdateEveryMs ?? 30_000;

  const warningAtMs = deps.warningAtMs ?? WARNING_AT_MS;
  const endAtMs = deps.endAtMs ?? END_AT_MS;

  const events = deps.events ?? DEFAULT_EVENTS;
  const sessionEndedPath = ref<string>('');

  sessionEndedPath.value = deps.sessionEndedPath ?? '/session-ended';

  let warningTimer: ReturnType<Window['setTimeout']> | null = null;
  let endTimer: ReturnType<Window['setTimeout']> | null = null;

  let tickInterval: ReturnType<Window['setInterval']> | null = null;
  let srInterval: ReturnType<Window['setInterval']> | null = null;

  let listening = false;

  function clearTimers() {
    if (warningTimer) win.clearTimeout(warningTimer);
    if (endTimer) win.clearTimeout(endTimer);
    warningTimer = null;
    endTimer = null;
  }

  function clearIntervals() {
    if (tickInterval) win.clearInterval(tickInterval);
    if (srInterval) win.clearInterval(srInterval);
    tickInterval = null;
    srInterval = null;
  }

  function scheduleFrom(activityMs: number) {
    clearTimers();
    if (!deps.store.isSessionActive) return;

    const warnIn = Math.max(0, warningAtMs - (now() - activityMs));
    const endIn = Math.max(0, endAtMs - (now() - activityMs));

    warningTimer = win.setTimeout(() => {
      if (!deps.store.isSessionActive) return;
      deps.store.showWarning();
    }, warnIn);

    endTimer = win.setTimeout(async () => {
      clearIntervals();

      try {
        const ok = (await deps.clearAppSessionData?.()) ?? true;
        // if cleanup failed, do NOT redirect to /session/expired
        if (!ok) return;

        deps.store.clearSession('timeout');
        await deps.router.push(sessionEndedPath.value);
      } catch (e) {
        return e;
      }
    }, endIn);
  }

  function shouldIgnoreActivity(e?: Event) {
    if (!e) return false;

    if (deps.ignoreEvent?.(e)) return true;

    const target = e.target;
    if (!(target instanceof Element)) return false;

    const selectors = deps.ignoreSelectors ?? [];
    return selectors.some((sel) => !!target.closest(sel));
  }

  function onActivity(e?: Event) {
    if (!deps.store.isSessionActive) return;
    if (e && shouldIgnoreActivity(e)) return;

    const t = now();
    deps.store.recordActivity(t);
    scheduleFrom(t);
  }

  function startTickingIfEnabled() {
    if (!countdownEnabled) return;

    clearIntervals();

    deps.store.updateTick(now());
    tickInterval = win.setInterval(() => {
      if (!deps.store.isSessionActive) return;
      deps.store.updateTick(now());
    }, tickEveryMs);

    // SR cadence can be slower; still calls updateTick so values stay aligned
    srInterval = win.setInterval(() => {
      if (!deps.store.isSessionActive) return;
      deps.store.updateTick(now());
    }, srUpdateEveryMs);
  }

  function start(overwrites?: { sessionEndedPath: string }) {
    if (!deps.store.isSessionActive) return;

    deps.store.setThresholds({ warningAtMs, endAtMs });

    sessionEndedPath.value = overwrites?.sessionEndedPath ?? sessionEndedPath.value;

    if (!listening) {
      events.forEach((e) => win.addEventListener(e, onActivity as any, { passive: true } as any));
      listening = true;
    }

    const base = deps.store.lastActivityAtMs ?? deps.store.sessionEstablishedAtMs ?? now();
    deps.store.recordActivity(base);
    scheduleFrom(base);

    startTickingIfEnabled();
  }

  function stop() {
    clearTimers();
    clearIntervals();

    if (listening) {
      events.forEach((e) => win.removeEventListener(e, onActivity as any));
      listening = false;
    }
  }

  function stayOnThisPage() {
    deps.store.dismissWarning();
    deps.closeWarningUi?.();
    onActivity();
  }

  function setLastActivityAgo(msAgo: number) {
    if (!deps.store.isSessionActive) return;

    const t = now() - msAgo;
    deps.store.recordActivity(t);
    scheduleFrom(t);
  }

  return {
    setLastActivityAgo,
    start,
    stop,
    stayOnThisPage,
  };
}
